import React, { useState, useEffect } from 'react';
import { Loader2, Save, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { api } from '../../lib/api';
import { toast } from 'sonner';

export default function AdminHaveYourSay() {
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/have-your-say').then((r) => setContent(r.data)); }, []);
  if (!content) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...content,
        sections: (content.sections || []).map((s, i) => ({ ...s, id: s.id || `sec-${i}` })),
        templates: (content.templates || []).map((t, i) => ({ ...t, id: t.id || `tpl-${i}` })),
      };
      await api.put('/admin/have-your-say', payload);
      toast.success('How to Have Your Say saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const upSection = (i, patch) => setContent({ ...content, sections: content.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const upTemplate = (i, patch) => setContent({ ...content, templates: content.templates.map((t, j) => (j === i ? { ...t, ...patch } : t)) });

  return (
    <div data-testid="admin-haveyoursay">
      <h1 className="font-serif font-black text-2xl text-[#1a2744] mb-1">How to Have Your Say</h1>
      <p className="text-sm text-slate-500 mb-4">Edit the public participation guide and the copy-ready templates residents use to contact Council.</p>

      <Card className="mb-4"><CardContent className="p-4">
        <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Intro text</label>
        <Textarea value={content.intro || ''} onChange={(e) => setContent({ ...content, intro: e.target.value })} rows={3} data-testid="haveyoursay-admin-intro" />
      </CardContent></Card>

      <div className="flex items-center justify-between mb-2">
        <p className="font-bold text-[#1a2744]">Guide sections ({(content.sections || []).length})</p>
        <Button type="button" variant="outline" onClick={() => setContent({ ...content, sections: [...(content.sections || []), { id: `sec-${Date.now()}`, title: '', body: '' }] })} className="h-10 font-bold btn-press" data-testid="haveyoursay-add-section"><Plus className="h-4 w-4 mr-1" /> Add section</Button>
      </div>
      <div className="space-y-3 mb-6">
        {(content.sections || []).map((s, i) => (
          <Card key={s.id || i} data-testid="haveyoursay-section-editor">
            <CardContent className="p-4 space-y-2">
              <div className="flex gap-2">
                <Input value={s.title} onChange={(e) => upSection(i, { title: e.target.value })} placeholder="Section title" className="h-10 font-bold" />
                <button type="button" onClick={() => setContent({ ...content, sections: content.sections.filter((_, j) => j !== i) })} className="text-red-600 p-2" aria-label="Remove section"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Textarea value={s.body} onChange={(e) => upSection(i, { body: e.target.value })} rows={3} placeholder="Plain-language explanation" />
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-between mb-2">
        <p className="font-bold text-[#1a2744]">Copy-ready templates ({(content.templates || []).length})</p>
        <Button type="button" variant="outline" onClick={() => setContent({ ...content, templates: [...(content.templates || []), { id: `tpl-${Date.now()}`, title: '', body: '' }] })} className="h-10 font-bold btn-press" data-testid="haveyoursay-add-template"><Plus className="h-4 w-4 mr-1" /> Add template</Button>
      </div>
      <div className="space-y-3 mb-6">
        {(content.templates || []).map((t, i) => (
          <Card key={t.id || i} data-testid="haveyoursay-template-editor">
            <CardContent className="p-4 space-y-2">
              <div className="flex gap-2">
                <Input value={t.title} onChange={(e) => upTemplate(i, { title: e.target.value })} placeholder="Template title" className="h-10 font-bold" />
                <button type="button" onClick={() => setContent({ ...content, templates: content.templates.filter((_, j) => j !== i) })} className="text-red-600 p-2" aria-label="Remove template"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Textarea value={t.body} onChange={(e) => upTemplate(i, { body: e.target.value })} rows={5} placeholder="Template text — use [square brackets] for fill-in-the-blank fields" className="font-mono text-xs" />
            </CardContent>
          </Card>
        ))}
      </div>

      <Button onClick={save} disabled={busy} className="h-12 w-full sm:w-auto bg-[#1a2744] text-white font-bold btn-press" data-testid="haveyoursay-save-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Guide
      </Button>
    </div>
  );
}
